/**
 * ServiceMapController — Enterprise Service Map Page Controller
 *
 * Sprint 49: Swapped ServiceMapVisualizer for FullviewConstellationVisualizer
 * Sprint 57: Interactive group filter pills (Business / Technology / Governance)
 * Sprint 58: Dynamic classification, governance pill as overlay toggle,
 *            uncategorised pill, getDynamicGroupMembers
 *
 * Coordinates ServiceMapDataBuilder (data pipeline) and
 * FullviewConstellationVisualizer (Cytoscape.js constellation engine).
 *
 * Following OOA/OOD principles:
 * - Template Method Pattern: Extends BaseController
 * - Mediator Pattern: Coordinates data builder and visualizer
 * - Single Responsibility: Page-level orchestration only
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import { ServiceMapDataBuilder, ServiceMapData } from '../../ea/service-map-data-builder';
import { FullviewConstellationVisualizer } from '../../ea/fullview-constellation-visualizer';
import {
    GROUP_DEFS, UNCATEGORISED_GROUP_DEF,
    classifyEntityType, getDynamicGroupMembers, getDisplayName,
    countEntitiesByGroup,
    type GroupId, type ExtendedGroupId, type GroupDef,
} from '../../ea/entity-type-groups';

/**
 * ServiceMapController — Manages the Enterprise Service Map page
 * with fullview constellation layout.
 */
export class ServiceMapController extends BaseController {
    private dataBuilder: ServiceMapDataBuilder;
    private visualizer: FullviewConstellationVisualizer | null = null;
    private currentData: ServiceMapData | null = null;
    private currentSessionId: string | undefined;

    // Sprint 57: Group filter state
    private activeGroups = new Set<GroupId>();
    private expandedGroups = new Set<ExtendedGroupId>();

    // Sprint 58: Governance overlay state (separate from highlight toggle)
    private governanceActive = false;

    constructor(services: ControllerServices) {
        super(services);
        this.dataBuilder = new ServiceMapDataBuilder(this.canisterClient);
    }

    getPageName(): PageName {
        return 'service-map' as PageName;
    }

    protected async doInit(): Promise<void> {
        await this.loadServiceMap();
    }

    protected setupEventListeners(): void {
        // Refresh button
        document.getElementById('btn-smap-refresh')?.addEventListener('click', () => {
            this.loadServiceMap();
        });

        // Export PNG button
        document.getElementById('btn-smap-export')?.addEventListener('click', () => {
            this.exportPNG();
        });

        // Session filter
        document.getElementById('smap-session-filter')?.addEventListener('change', (e) => {
            const sessionId = (e.target as HTMLSelectElement).value || undefined;
            this.currentSessionId = sessionId;
            this.loadServiceMap();
        });
    }

    protected doCleanup(): void {
        this.visualizer?.destroy();
        this.visualizer = null;
        this.currentData = null;
        this.activeGroups.clear();
        this.expandedGroups.clear();
        this.governanceActive = false;
    }

    // -----------------------------------------------------------------------
    // Data loading
    // -----------------------------------------------------------------------

    private async loadServiceMap(): Promise<void> {
        const container = document.getElementById('smap-cytoscape-container');
        const loadingEl = document.getElementById('smap-loading');
        const emptyEl = document.getElementById('smap-empty');

        if (!container) return;

        // Show loading
        loadingEl?.classList.remove('hidden');
        emptyEl?.classList.add('hidden');

        // Destroy previous visualizer
        this.visualizer?.destroy();
        this.visualizer = null;

        try {
            const data = await this.dataBuilder.build(this.currentSessionId);
            this.currentData = data;

            if (data.nodes.length === 0) {
                loadingEl?.classList.add('hidden');
                emptyEl?.classList.remove('hidden');
                this.renderGroupPills(data);
                this.refreshIcons();
                return;
            }

            emptyEl?.classList.add('hidden');

            // Sprint 57/58: Render interactive group pills (dynamic classification)
            this.renderGroupPills(data);

            // Populate session dropdown (first load only)
            this.populateSessionDropdown(data);

            // Sprint 49: Use FullviewConstellationVisualizer
            container.innerHTML = '';
            container.style.minHeight = '700px';
            container.style.height = '80vh';

            this.visualizer = new FullviewConstellationVisualizer('smap-cytoscape-container');
            await this.visualizer.render(data);

            // Listen for visualizer reset (clears group pills too)
            container.addEventListener('visualizer-reset', () => {
                this.activeGroups.clear();
                this.expandedGroups.clear();
                this.governanceActive = false;
                this.updatePillAppearance();
                this.collapseAllGroups();
            });

            // Hide loading — visualizer handles its own loading overlay
            loadingEl?.classList.add('hidden');

        } catch (error) {
            console.error('[ServiceMap] Failed to load service map:', error);
            loadingEl?.classList.add('hidden');
            if (container) {
                container.innerHTML = `
                    <div class="flex items-center justify-center h-64 text-center">
                        <div>
                            <i data-feather="alert-triangle" class="w-12 h-12 mx-auto mb-3 text-red-400"></i>
                            <p class="text-red-400">Failed to load service map</p>
                            <p class="text-sm text-slate-500 mt-1">${this.escapeHtml(String(error))}</p>
                        </div>
                    </div>`;
                this.refreshIcons();
            }
        }
    }

    // -----------------------------------------------------------------------
    // Sprint 57/58: Group Filter Pills (dynamic classification)
    // -----------------------------------------------------------------------

    private renderGroupPills(data: ServiceMapData): void {
        const totalEl = document.getElementById('smap-stat-total');
        if (totalEl) totalEl.textContent = String(data.statistics.totalEntities);

        const container = document.getElementById('smap-group-pills');
        if (!container) return;
        container.innerHTML = '';

        const groupCounts = countEntitiesByGroup(data.nodes);

        // Render the 3 fixed group pills
        for (const group of GROUP_DEFS) {
            const count = groupCounts.get(group.id) || 0;
            const pill = this.createGroupPill(group, count, data);
            container.appendChild(pill);
        }

        // Conditional 4th "Uncategorised" pill if count > 0
        const uncatCount = groupCounts.get('uncategorised') || 0;
        if (uncatCount > 0) {
            const pill = this.createGroupPill(UNCATEGORISED_GROUP_DEF, uncatCount, data);
            container.appendChild(pill);
        }

        this.refreshIcons();
    }

    private createGroupPill(group: GroupDef, count: number, data: ServiceMapData): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'inline-flex flex-col';
        wrapper.dataset.groupId = group.id;

        // Pill row: [icon + label + count] + [chevron]
        const row = document.createElement('div');
        row.className = 'inline-flex items-center gap-1';

        // Main pill button
        const pill = document.createElement('button');
        pill.className = 'smap-group-pill inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 border';
        pill.style.borderColor = group.color;
        pill.style.color = group.color;
        pill.style.backgroundColor = 'transparent';
        pill.dataset.groupId = group.id;

        pill.innerHTML = `<i data-feather="${group.icon}" class="w-3.5 h-3.5"></i><span>${group.label}</span><span class="font-semibold">${count}</span>`;

        pill.addEventListener('click', () => this.toggleGroup(group.id as GroupId));

        // Chevron button for expanding type chips
        const chevron = document.createElement('button');
        chevron.className = 'smap-group-chevron text-gray-400 hover:text-gray-600 transition-transform duration-150 p-0.5';
        chevron.innerHTML = '<i data-feather="chevron-down" class="w-3.5 h-3.5"></i>';
        chevron.style.transform = 'rotate(0deg)';
        chevron.dataset.groupId = group.id;
        chevron.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleGroupExpand(group.id as ExtendedGroupId, data);
        });

        row.append(pill, chevron);
        wrapper.appendChild(row);

        // Expanded type chips container (hidden by default)
        const chipsContainer = document.createElement('div');
        chipsContainer.className = 'smap-type-chips hidden mt-1 ml-1 flex flex-wrap gap-1';
        chipsContainer.dataset.groupId = group.id;
        wrapper.appendChild(chipsContainer);

        return wrapper;
    }

    private toggleGroup(groupId: GroupId): void {
        // Sprint 58: Governance pill toggles overlay visibility instead of highlight
        if (groupId === 'governance') {
            this.governanceActive = !this.governanceActive;
            this.visualizer?.toggleGovernanceOverlay(this.governanceActive);
            this.updatePillAppearance();
            // Rebuild legend to include/exclude governance types
            this.rebuildLegend();
            return;
        }

        if (this.activeGroups.has(groupId)) {
            this.activeGroups.delete(groupId);
        } else {
            this.activeGroups.add(groupId);
        }
        this.applyGroupHighlighting();
        this.updatePillAppearance();
    }

    private applyGroupHighlighting(): void {
        if (!this.visualizer || !this.currentData) return;

        if (this.activeGroups.size === 0) {
            this.visualizer.clearGroupHighlight();
            return;
        }

        // Collect all entity types from all active groups using dynamic classification
        const highlightedTypes = new Set<string>();
        for (const node of this.currentData.nodes) {
            const normalized = node.entityType.toLowerCase().replace(/\s+/g, '-');
            const nodeGroup = classifyEntityType(normalized);
            if (this.activeGroups.has(nodeGroup as GroupId)) {
                highlightedTypes.add(normalized);
                // Add alternate form
                if (normalized.includes('-')) highlightedTypes.add(normalized.replace(/-/g, '_'));
                if (normalized.includes('_')) highlightedTypes.add(normalized.replace(/_/g, '-'));
            }
        }

        this.visualizer.applyGroupHighlight(highlightedTypes);
    }

    private updatePillAppearance(): void {
        const pills = document.querySelectorAll<HTMLButtonElement>('.smap-group-pill');
        const hasActive = this.activeGroups.size > 0;

        for (const pill of pills) {
            const gid = pill.dataset.groupId as string;
            const group = GROUP_DEFS.find(g => g.id === gid)
                || (gid === 'uncategorised' ? UNCATEGORISED_GROUP_DEF : undefined);
            if (!group) continue;

            // Sprint 58: Governance pill has separate toggle behaviour
            if (gid === 'governance') {
                if (this.governanceActive) {
                    // Active: filled background, white text
                    pill.style.backgroundColor = group.color;
                    pill.style.color = '#ffffff';
                    pill.style.opacity = '1';
                } else {
                    // Inactive: normal appearance (not dimmed by business/tech selection)
                    pill.style.backgroundColor = 'transparent';
                    pill.style.color = group.color;
                    pill.style.opacity = hasActive ? '0.6' : '1';
                }
                continue;
            }

            // Uncategorised pill follows same highlight/dim as business/technology
            if (gid === 'uncategorised') {
                pill.style.backgroundColor = 'transparent';
                pill.style.color = group.color;
                pill.style.opacity = hasActive ? '0.4' : '1';
                continue;
            }

            if (this.activeGroups.has(gid as GroupId)) {
                // Active: filled background, white text
                pill.style.backgroundColor = group.color;
                pill.style.color = '#ffffff';
                pill.style.opacity = '1';
            } else if (hasActive) {
                // Inactive while others active: dimmed
                pill.style.backgroundColor = 'transparent';
                pill.style.color = group.color;
                pill.style.opacity = '0.4';
            } else {
                // Nothing active: normal state
                pill.style.backgroundColor = 'transparent';
                pill.style.color = group.color;
                pill.style.opacity = '1';
            }
        }
    }

    private toggleGroupExpand(groupId: ExtendedGroupId, data: ServiceMapData): void {
        const chipsContainer = document.querySelector<HTMLElement>(`.smap-type-chips[data-group-id="${groupId}"]`);
        const chevron = document.querySelector<HTMLElement>(`.smap-group-chevron[data-group-id="${groupId}"]`);
        if (!chipsContainer) return;

        if (this.expandedGroups.has(groupId)) {
            // Collapse
            this.expandedGroups.delete(groupId);
            chipsContainer.classList.add('hidden');
            if (chevron) chevron.style.transform = 'rotate(0deg)';
            return;
        }

        // Expand
        this.expandedGroups.add(groupId);
        chipsContainer.classList.remove('hidden');
        if (chevron) chevron.style.transform = 'rotate(180deg)';

        // Populate chips if empty
        if (chipsContainer.children.length > 0) return;

        // Sprint 58: Use dynamic group members from actual node data
        const members = getDynamicGroupMembers(groupId, data.nodes);
        const group = GROUP_DEFS.find(g => g.id === groupId)
            || (groupId === 'uncategorised' ? UNCATEGORISED_GROUP_DEF : undefined);
        const groupColor = group?.color || '#6b7280';

        for (const member of members) {
            if (member.count === 0) continue;

            const chip = document.createElement('button');
            chip.className = 'smap-type-chip inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border transition-all duration-150';
            chip.style.borderColor = groupColor;
            chip.style.color = groupColor;
            chip.style.backgroundColor = 'transparent';
            chip.dataset.cyKey = member.cyKey;
            chip.textContent = `${member.displayName} (${member.count})`;

            chip.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleTypeChip(member.cyKey, chip, groupColor);
            });

            chipsContainer.appendChild(chip);
        }
    }

    private toggleTypeChip(cyKey: string, chip: HTMLButtonElement, groupColor: string): void {
        if (!this.visualizer) return;
        this.visualizer.toggleSingleEntityType(cyKey);

        const isHidden = this.visualizer.isEntityTypeHidden(cyKey);
        if (isHidden) {
            chip.style.backgroundColor = 'transparent';
            chip.style.color = '#9ca3af';
            chip.style.borderColor = '#d1d5db';
            chip.style.textDecoration = 'line-through';
        } else {
            chip.style.backgroundColor = 'transparent';
            chip.style.color = groupColor;
            chip.style.borderColor = groupColor;
            chip.style.textDecoration = 'none';
        }
    }

    private collapseAllGroups(): void {
        const chips = document.querySelectorAll<HTMLElement>('.smap-type-chips');
        chips.forEach(c => c.classList.add('hidden'));
        const chevrons = document.querySelectorAll<HTMLElement>('.smap-group-chevron');
        chevrons.forEach(c => c.style.transform = 'rotate(0deg)');
    }

    /** Sprint 58: Rebuild the visualizer legend (e.g. after governance toggle) */
    private rebuildLegend(): void {
        if (!this.visualizer || !this.currentData) return;
        // Remove existing legend and rebuild
        const existingLegend = document.getElementById('fv-legend');
        if (existingLegend) existingLegend.remove();
        // The visualizer's buildLegend is private, so we trigger a
        // lightweight rebuild by destroying and re-rendering just the legend.
        // Instead, we call the public destroy/render cycle on the overlays
        // section. Since buildLegend is private, we'll directly manipulate.
        // Actually, let's just remove the old legend — the visualizer
        // already filters governance in buildLegend based on governanceVisible.
        // We need to re-invoke buildOverlays. Since it's private, we'll
        // reconstruct the legend here in the controller.
        this.rebuildLegendDirectly();
    }

    private rebuildLegendDirectly(): void {
        if (!this.currentData) return;
        const container = document.getElementById('smap-cytoscape-container');
        if (!container) return;

        // Remove old legend
        document.getElementById('fv-legend')?.remove();

        const typeCounts = new Map<string, number>();
        for (const n of this.currentData.nodes) {
            typeCounts.set(n.entityType, (typeCounts.get(n.entityType) || 0) + 1);
        }

        const legend = document.createElement('div');
        legend.id = 'fv-legend';
        legend.style.cssText =
            'position:absolute;bottom:12px;left:12px;background:rgba(30,41,59,0.92);' +
            'border:1px solid #334155;border-radius:10px;padding:10px 14px;z-index:10;' +
            'font-family:system-ui,sans-serif;font-size:12px;color:#cbd5e1;' +
            'max-height:60vh;overflow-y:auto;max-width:220px;';
        for (const evt of ['mousedown', 'pointerdown', 'touchstart', 'click'] as const) {
            legend.addEventListener(evt, (e: Event) => e.stopPropagation());
        }

        const title = document.createElement('div');
        title.textContent = 'Entity Types';
        title.style.cssText = 'font-weight:600;margin-bottom:6px;color:#f1f5f9;font-size:13px;';
        legend.appendChild(title);

        const sorted = Array.from(typeCounts.entries())
            .filter(([type]) => {
                if (!this.governanceActive && classifyEntityType(type) === 'governance') {
                    return false;
                }
                return true;
            })
            .sort((a, b) => b[1] - a[1]);

        for (const [type, count] of sorted) {
            const color = this.getEntityColorForLegend(type);
            const row = document.createElement('div');
            row.style.cssText =
                'display:flex;align-items:center;gap:8px;padding:3px 0;cursor:pointer;transition:opacity 0.2s;';

            const dot = document.createElement('span');
            dot.style.cssText =
                `display:inline-block;width:12px;height:12px;border-radius:50%;background:${color};flex-shrink:0;`;

            const label = document.createElement('span');
            label.textContent = `${type} (${count})`;

            row.append(dot, label);
            row.onclick = () => {
                if (!this.visualizer) return;
                this.visualizer.toggleSingleEntityType(type);
                const isHidden = this.visualizer.isEntityTypeHidden(type);
                row.style.opacity = isHidden ? '0.3' : '1';
            };
            legend.appendChild(row);
        }

        container.appendChild(legend);
    }

    /** Simple color lookup matching the visualizer's ENTITY_COLORS */
    private getEntityColorForLegend(entityType: string): string {
        const colors: Record<string, string> = {
            'division': '#facc15', 'department': '#60a5fa', 'application': '#f59e0b',
            'infrastructure': '#10b981', 'data': '#ef4444', 'technical-service': '#8b5cf6',
            'hr-role': '#ec4899', 'business-service': '#14b8a6', 'risk': '#f97316',
            'control': '#22c55e', 'capability': '#06b6d4', 'process': '#a855f7',
            'process_connector': '#e11d48', 'compliance': '#84cc16', 'integration': '#d946ef',
            'vendor': '#0ea5e9', 'organization': '#3b82f6',
        };
        const normalized = entityType.toLowerCase().replace(/\s+/g, '-');
        return colors[normalized] || '#6b7280';
    }

    // -----------------------------------------------------------------------
    // Session dropdown
    // -----------------------------------------------------------------------

    private populateSessionDropdown(data: ServiceMapData): void {
        const select = document.getElementById('smap-session-filter') as HTMLSelectElement;
        if (!select) return;

        // Only populate once (if already has options beyond "All Sessions")
        if (select.options.length > 1) return;

        // Extract unique session IDs from node metadata
        const sessionIds = new Set<string>();
        for (const node of data.nodes) {
            const sessionId = node.metadata['_import_session_id'];
            if (sessionId) {
                sessionIds.add(sessionId);
            }
        }

        // Add options sorted alphabetically
        const sorted = Array.from(sessionIds).sort();
        for (const sid of sorted) {
            const option = document.createElement('option');
            option.value = sid;
            option.textContent = sid;
            select.appendChild(option);
        }
    }

    // -----------------------------------------------------------------------
    // Export
    // -----------------------------------------------------------------------

    private exportPNG(): void {
        if (!this.visualizer) {
            (window as any).showToast?.('No service map to export', 'warning');
            return;
        }

        this.visualizer.exportPNG();
        (window as any).showToast?.('Service map exported', 'success');
    }
}
